/**
 * Product capture is a screenshot of what is on screen: the map canvas at
 * its live box, then each HUD / callout / slider stamped at the same
 * getBoundingClientRect it has in the UI. We do not let html2canvas relayout
 * the whole column (flex + ignored canvases collapse the map and drift overlays).
 *
 * Toolbars and dialogs opt out with `data-export-hide` / `data-hud-controls`.
 */

import { loadHtml2Canvas } from './exportLibs';

export interface ProductCaptureResult {
  dataUrl: string;
  width: number;
  height: number;
  aspectRatio: number;
}

export interface ProductCaptureOptions {
  scale?: number;
  hideSelectors?: string[];
}

const DEFAULT_HIDE = [
  '[data-export-hide]',
  '[data-hud-controls]',
  '.mapboxgl-ctrl-geocoder',
  '.mapboxgl-ctrl-top-left',
  '.mapboxgl-ctrl-top-right',
  '.MuiTooltip-popper',
  '.MuiPopover-root',
  '.MuiDialog-root',
  '.MuiMenu-root',
];

const MAP_BOX_SEL = '#pathfinder-map-container';

async function settleMap(): Promise<void> {
  const map: any = (window as any).__pathfinderMap;
  if (!map) return;
  try {
    if (map.isMoving?.()) {
      await new Promise<void>((resolve) => {
        map.once('moveend', resolve);
        setTimeout(resolve, 1500);
      });
    }
    if (!map.areTilesLoaded?.()) {
      await new Promise<void>((resolve) => {
        map.once('idle', resolve);
        setTimeout(resolve, 2500);
      });
    }
    await new Promise<void>((resolve) => {
      map.once('render', () => resolve());
      map.triggerRepaint?.();
      setTimeout(resolve, 400);
    });
  } catch {
    /* best effort */
  }
}

function matchesHide(el: Element, hide: string[]): boolean {
  return hide.some((sel) => {
    try {
      return el.matches(sel) || Boolean(el.closest(sel));
    } catch {
      return false;
    }
  });
}

function hideMatching(root: ParentNode, selectors: string[]): Array<{ el: HTMLElement; visibility: string }> {
  const seen = new Set<HTMLElement>();
  const out: Array<{ el: HTMLElement; visibility: string }> = [];
  for (const sel of selectors) {
    try {
      root.querySelectorAll<HTMLElement>(sel).forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        out.push({ el, visibility: el.style.visibility });
        el.style.visibility = 'hidden';
      });
    } catch {
      /* invalid selector */
    }
  }
  return out;
}

function restoreHidden(hidden: Array<{ el: HTMLElement; visibility: string }>): void {
  hidden.forEach(({ el, visibility }) => {
    el.style.visibility = visibility;
  });
}

function resolveBackground(el: HTMLElement): string {
  const bg = window.getComputedStyle(el).backgroundColor;
  if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') return bg;
  return '#111418';
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

type StyleSnap = { el: HTMLElement; css: Partial<Record<'width' | 'height' | 'minWidth' | 'minHeight' | 'maxWidth' | 'maxHeight' | 'flex', string>> };

/** Pin flex/map boxes to their on-screen pixel size so a clone cannot collapse. */
function freezeLayout(root: HTMLElement): () => void {
  const els: HTMLElement[] = [root];
  const mapBox = root.querySelector(MAP_BOX_SEL);
  if (mapBox instanceof HTMLElement) {
    els.push(mapBox);
    let walk: HTMLElement | null = mapBox.parentElement;
    while (walk && walk !== root) {
      els.push(walk);
      walk = walk.parentElement;
    }
  }
  root.querySelectorAll<HTMLElement>('.mapboxgl-map, .maplibregl-map').forEach((el) => els.push(el));

  const snaps: StyleSnap[] = els.map((el) => {
    const r = el.getBoundingClientRect();
    const css = {
      width: el.style.width,
      height: el.style.height,
      minWidth: el.style.minWidth,
      minHeight: el.style.minHeight,
      maxWidth: el.style.maxWidth,
      maxHeight: el.style.maxHeight,
      flex: el.style.flex,
    };
    const w = `${Math.round(r.width)}px`;
    const h = `${Math.round(r.height)}px`;
    el.style.width = w;
    el.style.height = h;
    el.style.minWidth = w;
    el.style.minHeight = h;
    el.style.maxWidth = w;
    el.style.maxHeight = h;
    el.style.flex = 'none';
    return { el, css };
  });

  return () => {
    snaps.forEach(({ el, css }) => {
      el.style.width = css.width ?? '';
      el.style.height = css.height ?? '';
      el.style.minWidth = css.minWidth ?? '';
      el.style.minHeight = css.minHeight ?? '';
      el.style.maxWidth = css.maxWidth ?? '';
      el.style.maxHeight = css.maxHeight ?? '';
      el.style.flex = css.flex ?? '';
    });
  };
}

function drawAt(ctx: CanvasRenderingContext2D, img: CanvasImageSource, root: DOMRect, box: DOMRect, scale: number): void {
  ctx.drawImage(
    img,
    (box.left - root.left) * scale,
    (box.top - root.top) * scale,
    box.width * scale,
    box.height * scale,
  );
}

/** Flow siblings of the map (time slider, etc.) — stamp each as one card. */
function collectSiblingStamps(root: HTMLElement, mapBox: Element | null, hide: string[]): HTMLElement[] {
  const stamps: HTMLElement[] = [];
  const walk = (el: HTMLElement) => {
    if (mapBox && (el === mapBox || mapBox.contains(el))) return;
    if (mapBox && el.contains(mapBox)) {
      Array.from(el.children).forEach((c) => walk(c as HTMLElement));
      return;
    }
    if (matchesHide(el, hide)) return;
    const cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return;
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;
    stamps.push(el);
  };
  Array.from(root.children).forEach((c) => walk(c as HTMLElement));
  return stamps;
}

export async function captureProductView(root: HTMLElement, opts: ProductCaptureOptions = {}): Promise<ProductCaptureResult | null> {
  const scale = opts.scale ?? 2;
  const hide = [...DEFAULT_HIDE, ...(opts.hideSelectors ?? [])];
  await settleMap();

  const hidden = hideMatching(root, hide);
  const unfreeze = freezeLayout(root);
  try {
    await nextFrame();

    const rootRect = root.getBoundingClientRect();
    const width = Math.round(rootRect.width);
    const height = Math.round(rootRect.height);
    if (width === 0 || height === 0) return null;

    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(width * scale));
    out.height = Math.max(1, Math.round(height * scale));
    const ctx = out.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = resolveBackground(root);
    ctx.fillRect(0, 0, out.width, out.height);

    // 1. Map (and any inset) canvases at their live on-screen boxes.
    root.querySelectorAll('canvas').forEach((c) => {
      if (c.width === 0 || c.height === 0) return;
      const box = c.getBoundingClientRect();
      if (box.width < 1 || box.height < 1) return;
      try {
        drawAt(ctx, c, rootRect, box, scale);
      } catch {
        /* tainted */
      }
    });

    const html2canvas = await loadHtml2Canvas();
    const stamp = async (el: HTMLElement) => {
      const box = el.getBoundingClientRect();
      if (box.width < 2 || box.height < 2) return;
      try {
        const shot = await html2canvas(el, {
          backgroundColor: null,
          scale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: Math.round(box.width),
          height: Math.round(box.height),
          windowWidth: Math.round(box.width),
          windowHeight: Math.round(box.height),
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
          ignoreElements: (node) => node instanceof Element && (node.tagName === 'CANVAS' || matchesHide(node, hide)),
          onclone: (_doc, cloned) => {
            if (!(cloned instanceof HTMLElement)) return;
            cloned.style.width = `${Math.round(box.width)}px`;
            cloned.style.height = `${Math.round(box.height)}px`;
            cloned.style.background = 'transparent';
            cloned.style.backgroundColor = 'transparent';
            cloned.querySelectorAll<HTMLElement>('.mapboxgl-map, .mapboxgl-canvas-container, .maplibregl-map, .maplibregl-canvas-container').forEach((n) => {
              n.style.background = 'transparent';
              n.style.backgroundColor = 'transparent';
            });
            cloned.querySelectorAll<HTMLElement>('*').forEach((n) => {
              if (n.style.backdropFilter || (n.style as any).webkitBackdropFilter) {
                n.style.backdropFilter = 'none';
                (n.style as any).webkitBackdropFilter = 'none';
              }
            });
          },
        });
        drawAt(ctx, shot, rootRect, box, scale);
      } catch (e) {
        console.warn('[Product capture] overlay stamp failed', e);
      }
    };

    // 2. Everything floating on the map (HUD, callouts, legend, markers) at
    //    the map container's live box — same origin the cards were positioned in.
    const mapBox = root.querySelector(MAP_BOX_SEL);
    if (mapBox instanceof HTMLElement) {
      await stamp(mapBox);
    }

    // 3. Time slider and any other sibling chrome, at their live boxes.
    for (const el of collectSiblingStamps(root, mapBox, hide)) {
      await stamp(el);
    }

    return {
      dataUrl: out.toDataURL('image/png'),
      width: out.width,
      height: out.height,
      aspectRatio: out.width / out.height,
    };
  } catch (e) {
    console.error('[Product capture] failed:', e);
    return null;
  } finally {
    unfreeze();
    restoreHidden(hidden);
  }
}

export function productCaptureRoot(fallback: HTMLElement | null): HTMLElement | null {
  return (document.fullscreenElement as HTMLElement | null) ?? fallback;
}
